import React, { useMemo, useState } from "react";
// import {
//   ArrowLeft,
//   Check,
//   Info,
//   Search,
//   SlidersHorizontal,
//   Sparkles,
// } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useMainStore } from "@/stores";
// import CommonAvatar from "@/shared/components/ui/Avatar/CommonAvatar";
// import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
// import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
// import Loader from "@/shared/components/ui/Loaders/Loader";
// import CommonInput from "@/shared/components/ui/Input/CommonInput";
// import CommonSelect from "@/shared/components/ui/Select/CommonSelect";
// import AddAccountModal from "./AddAccountModal";
import {
  useCreateCustomAccountQuery,
  useCreateAccountFromContacts,
  useFetchCustomAccountQueries,
  useFetchAccounts,
  useRunCustomAccountQuery,
  useSelectCustomAccountQuery,
} from "../hooks/useAccounts";
import {
  useGetAccountsDashboard,
  useGetSimilarAccountTargets,
} from "@/features/dashboard/hooks/useGetDashboardApi";
// import SimilarAccountActionButton from "@/features/dashboard/components/accountsDashboard/similarAccounts/SimilarAccountActionButton";
import {
  getAccountProfilePath,
  getDominantTopEngagedChannel,
  safeFormatTimeAgo,
  // sanitizeCompanyLogoUrl,
  summarizeChannelMetricsLine,
  summarizeTopEngagedScoreBreakdown,
  topEngagedChannelTitle,
} from "@/features/dashboard/utils/accountsDashboardUtils";
import { AccountDiscoveryTabs, getCleanName } from "@/shared/utils/accountConfig";
import { BreadCrumbs } from "@/shared/components/ui/BreadCrumb/BreadCrumbs";
import { UnderlineTabs } from "@/shared/components/ui/Tabs";
import SquircleIcon from "@/shared/components/ui/SquircleIcon";
import Search from "@/assets/svg/Search";
import CustomSearch from "./AccountDiscovery/CustomSearch";
import CustomSearchResultsTable from "./AccountDiscovery/CustomSearchResultsTable";
import DiscoveryResultsTable from "./AccountDiscovery/results/DiscoveryResultsTable";
import TextButton from "@/shared/components/ui/Button/TextButton";
import { formatUsdMillions } from "./AccountDiscovery/results/utils";

const TABS = [
  { id: "custom", label: "Custom query", available: true },
  { id: "engaged", label: "Most engaged + likely to win", available: true },
  { id: "lookalike", label: "Look-alike accounts", available: true },
  { id: "intent", label: "Intent matched", available: false },
];

const COMBINED_SCORE_WEIGHTS = {
  engagement: 0.5,
  commercial: 0.25,
  relationship: 0.15,
  intent: 0.1,
};

const SCORE_LABELS = {
  email_open: "Email opens",
  email_click: "Email clicks",
  message_read: "Message reads",
  reply: "Replies",
  linkedin_connect: "LinkedIn connections",
  reply_floor: "Reply baseline",
};

const COMMERCIAL_SCORE_LABELS = {
  reply_rate: "Reply rate",
  win_status: "Won status",
  deals: "Deal history",
  latest_meeting: "Meeting recency",
  won_deals: "Won deals",
  open_deals: "Open deals",
};

const formatNumber = (value) =>
  new Intl.NumberFormat().format(Number(value) || 0);

const getAccountName = (account) =>
  account?.company_name || getCleanName(account?.domain) || "Unnamed account";

const getIndustry = (account) =>
  account?.industry || account?.company_industry || "Industry not identified";

// The look-alike's own similarity (scored factor by factor in research). reference_score is
// the seed account's commercial score and says nothing about how similar a look-alike is.
const getLookalikeScore = (account) => {
  const score = Number(account?.similarity_score);
  return Number.isFinite(score) ? Math.max(0, Math.min(100, score)) : 0;
};

const getWhySurfaced = (account) => {
  if (account?.is_custom_query) {
    return account?.reason || "Matches the saved custom account query";
  }
  if (account?.is_lookalike) {
    const reference = account?.similarity_reference_name
      ? `Similar to ${account.similarity_reference_name}`
      : "Matches your strongest engaged accounts";
    return account?.reason ? `${reference}: ${account.reason}` : reference;
  }
  const dominantChannel = getDominantTopEngagedChannel(account?.signal_counts);
  const outreachLine = dominantChannel
    ? summarizeChannelMetricsLine(account?.channel_metrics, dominantChannel)
    : null;
  const scoreLine = summarizeTopEngagedScoreBreakdown(account?.score_breakdown);
  const commercialLine = account?.commercial_evidence;

  if (outreachLine && dominantChannel && commercialLine) {
    return `${topEngagedChannelTitle(dominantChannel)}: ${outreachLine}; ${commercialLine}`;
  }
  if (outreachLine && dominantChannel) {
    return `${topEngagedChannelTitle(dominantChannel)}: ${outreachLine}`;
  }
  if (commercialLine) return commercialLine;
  if (scoreLine) return scoreLine;
  return "Recent account activity was detected";
};

const getDrivers = (account) => {
  const customDrivers = account?.is_custom_query
    ? [{
      key: "custom_query_score",
      label: "Custom query fit",
      points: Number(account?.similarity_score) || 0,
      detail: "public-web research",
      displayValue: `${Math.round(Number(account?.similarity_score) || 0)}`,
    }]
    : [];
  const lookalikeDrivers = account?.is_lookalike
    ? [{
      key: "similarity_score",
      label: "Similarity to winning accounts",
      points: Number(account?.similarity_score) || 0,
      detail: account?.similarity_reference_name
        ? `based on ${account.similarity_reference_name}`
        : null,
      displayValue: `${Math.round(Number(account?.similarity_score) || 0)}`,
    }]
    : [];
  const engagementDrivers =
    account?.score_breakdown && typeof account.score_breakdown === "object"
      ? Object.entries(account.score_breakdown).map(([key, item]) => ({
        key,
        label: SCORE_LABELS[key] || key.replaceAll("_", " "),
        points: Number(item?.points) || 0,
        detail: Number(item?.count) ? `${Number(item.count)} signals` : null,
        displayValue: `+${Number(item?.points) || 0}`,
      }))
      : [];
  const commercialDrivers =
    account?.win_score_breakdown && typeof account.win_score_breakdown === "object"
      ? Object.entries(account.win_score_breakdown).map(([key, value]) => ({
        key: `commercial_${key}`,
        label: COMMERCIAL_SCORE_LABELS[key] || key.replaceAll("_", " "),
        points: Number(value) || 0,
        detail: "commercial evidence",
        displayValue: `${Math.round(Number(value) || 0)}`,
      }))
      : [];
  const accountDrivers = [
    {
      key: "relationship_score",
      label: "Relationship strength",
      points: Number(account?.relationship_score) || 0,
      detail: account?.strong_relationship_count
        ? `${account.strong_relationship_count} strong`
        : null,
      displayValue: `${Math.round(Number(account?.relationship_score) || 0)}`,
    },
    {
      key: "stakeholder_coverage_score",
      label: "Stakeholder coverage",
      points: Number(account?.stakeholder_coverage_score) || 0,
      detail: account?.stakeholder_count
        ? `${account.stakeholder_count} stakeholders`
        : null,
      displayValue: `${Math.round(Number(account?.stakeholder_coverage_score) || 0)}`,
    },
    {
      key: "intent_score",
      label: "Campaign intent",
      points: Number(account?.intent_score) || 0,
      detail: account?.matched_intent_trigger_count
        ? `${account.matched_intent_trigger_count} triggers`
        : null,
      displayValue: `${Math.round(Number(account?.intent_score) || 0)}`,
    },
  ];

  return [...customDrivers, ...lookalikeDrivers, ...engagementDrivers, ...commercialDrivers, ...accountDrivers]
    .filter((item) => item.points > 0)
    .sort((a, b) => b.points - a.points)
    .slice(0, 4);
};

const normalizeAccountKey = (value) => String(value || "").trim().toLowerCase();

const getAccountKeys = (account) =>
  [account?.account_id, account?.domain, account?.company_name]
    .map(normalizeAccountKey)
    .filter(Boolean);

const getCommercialEvidence = (account, reference) => {
  if (!reference && !account?.commercial_score) return null;
  const parts = [];
  if (account?.account_is_won || reference?.is_won) parts.push("Won account");
  if (Number(account?.won_deal_count) > 0) {
    parts.push(`${account.won_deal_count} won deal${Number(account.won_deal_count) === 1 ? "" : "s"}`);
  } else if (Number(account?.deal_count || reference?.deal_count) > 0) {
    const dealCount = Number(account?.deal_count || reference?.deal_count);
    parts.push(`${dealCount} deal${dealCount === 1 ? "" : "s"}`);
  }
  if (Number(account?.open_deal_count) > 0) {
    parts.push(`${account.open_deal_count} open`);
  }
  if (Number(reference?.reply_rate) > 0) {
    parts.push(`${Math.round(Number(reference.reply_rate))}% reply rate`);
  }
  if (reference?.latest_meeting_at || account?.latest_meeting_at) parts.push("recent meeting");
  return parts.length ? parts.join(" · ") : "Commercial evidence available";
};

const getSafeEvidenceUrl = (value) => {
  if (!value) return null;
  try {
    const url = new URL(String(value));
    return ["http:", "https:"].includes(url.protocol) ? url.toString() : null;
  } catch {
    return null;
  }
};

const getEvidence = (account) => {
  const evidence = [];
  if (account?.is_custom_query) {
    (Array.isArray(account?.evidence) ? account.evidence : []).slice(0, 2).forEach((item) => {
      const href = getSafeEvidenceUrl(item?.source_url);
      if (item?.source_url && !href) {
        console.warn(
          "[CustomAccountQueries][frontend] ignored invalid evidence URL",
          JSON.stringify({ sourceUrl: item.source_url }),
        );
      }
      evidence.push({
        label: item?.title || "Research evidence",
        value: item?.summary || "Public evidence matched this query.",
        href,
      });
    });
    if (account?.last_signal_at) {
      evidence.push({
        label: "Last researched",
        value: safeFormatTimeAgo(account.last_signal_at),
      });
    }
    return evidence.slice(0, 3);
  }
  if (account?.is_lookalike) {
    if (account?.similarity_reference_name) {
      evidence.push({
        label: "Reference account",
        value: account.similarity_reference_name,
      });
    }
    if (account?.reason) {
      evidence.push({ label: "Why it surfaced", value: account.reason });
    }
    if (account?.last_signal_at) {
      evidence.push({
        label: "Last synced",
        value: safeFormatTimeAgo(account.last_signal_at),
      });
    }
    return evidence.slice(0, 3);
  }
  const dominantChannel = getDominantTopEngagedChannel(account?.signal_counts);
  const outreachLine = dominantChannel
    ? summarizeChannelMetricsLine(account?.channel_metrics, dominantChannel)
    : null;

  if (outreachLine && dominantChannel) {
    evidence.push({
      label: `${topEngagedChannelTitle(dominantChannel)} activity`,
      value: outreachLine,
    });
  }

  const scoreLine = summarizeTopEngagedScoreBreakdown(account?.score_breakdown);
  if (scoreLine) evidence.push({ label: "Score inputs", value: scoreLine });

  if (account?.commercial_evidence) {
    evidence.push({
      label: `Commercial evidence · ${Math.round(Number(account.commercial_score) || 0)}/100`,
      value: account.commercial_evidence,
    });
  }

  if (Number(account?.relationship_score) > 0 || Number(account?.stakeholder_coverage_score) > 0) {
    evidence.push({
      label: "Relationship coverage",
      value: `${account.stakeholder_count || 0} stakeholders · ${account.engaged_stakeholder_count || 0} engaged · ${account.strong_relationship_count || 0} strong relationships`,
    });
  }

  if (Number(account?.intent_score) > 0) {
    evidence.push({
      label: "Campaign intent",
      value: `${account.matched_intent_trigger_count || 0} matched triggers across ${account.matched_intent_campaign_count || 0} campaigns`,
    });
  }

  if (account?.last_signal_at) {
    evidence.push({
      label: "Last signal",
      value: safeFormatTimeAgo(account.last_signal_at),
    });
  }

  return evidence.slice(0, 3);
};

const ScoreBar = ({ value, className = "bg-secondary" }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
    <div
      className={`h-full rounded-full ${className}`}
      style={{ width: `${Math.max(0, Math.min(100, Number(value) || 0))}%` }}
    />
  </div>
);

/**
 * Find new accounts: custom search, look-alikes and most engaged.
 * `embedded` renders it inside the Find page (no breadcrumb or title, sub-tab in the
 * URL's `subTab` under `basePath`); `isActive` stops a hidden instance from redirecting.
 */
export default function AccountDiscovery({
  embedded = false,
  basePath = "/accounts/find",
  isActive = true,
} = {}) {
  const navigate = useNavigate();
  const user = useMainStore((state) => state.user);
  const params = useParams();
  const tab = embedded ? params.subTab : params.tab;
  const currentTab = useMemo(() => {
    if (!tab) return "custom_search";
    if (tab === "custom") return "custom_search";
    if (tab === "lookalike") return "find_look_alikes";
    if (tab === "engaged") return "most_engaged_plus_likely_to_win";
    if (tab === "intent") return "intent_matched";
    return tab;
  }, [tab]);
  const [activeTab, setActiveTab] = useState(currentTab);

  React.useEffect(() => {
    setActiveTab(currentTab);
  }, [currentTab]);

  React.useEffect(() => {
    // Intent matched is not available yet; its tab is disabled, so a direct link goes to search.
    if (!isActive) return;
    if (!tab || currentTab === "intent_matched") {
      navigate(`${basePath}/custom_search`, { replace: true });
    }
  }, [tab, currentTab, navigate, isActive, basePath]);
  const [mode, setMode] = useState("screen");
  const windowDays = 30;
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [createdAccountIds, setCreatedAccountIds] = useState(() => new Set());
  const [customQueryText, setCustomQueryText] = useState("");
  const [customQueryName, setCustomQueryName] = useState("");
  const [selectedCustomQueryId, setSelectedCustomQueryId] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);

  const { data: accountsData = [] } = useFetchAccounts({
    userId: user?.id,
    enabled: Boolean(user?.id),
  });
  const {
    data: dashboardData,
    isLoading: isLoadingDashboard,
    isError: isDashboardError,
  } = useGetAccountsDashboard(windowDays);
  const {
    data: similarTargetData,
    isLoading: isLoadingSimilarAccounts,
    isError: isSimilarAccountsError,
  } = useGetSimilarAccountTargets(
    activeTab === "find_look_alikes" || activeTab === "lookalike" ? user?.id : undefined,
  );
  const { mutate: createAccount, isPending: isCreatingAccount } =
    useCreateAccountFromContacts();
  const { data: customQueries = [], isLoading: isLoadingCustomQueries, isError: isCustomQueriesError } =
    useFetchCustomAccountQueries({ userId: user?.id });
  const { mutate: createCustomQuery, isPending: isCreatingCustomQuery } =
    useCreateCustomAccountQuery({
      action: (data) => setSelectedCustomQueryId(data?.id || ""),
    });
  const { mutate: runCustomQuery, isPending: isRunningCustomQuery } =
    useRunCustomAccountQuery();
  const { mutate: selectCustomQuery, isPending: isSelectingCustomQuery } =
    useSelectCustomAccountQuery();

  const similarAccounts = Array.isArray(similarTargetData?.similar_accounts)
    ? similarTargetData.similar_accounts
    : [];
  const lookalikeAccounts = similarAccounts.map((account) => {
    const score = getLookalikeScore(account);
    return {
      ...account,
      is_lookalike: true,
      company_name: account?.company_name || account?.company_domain || "Unnamed account",
      domain: account?.company_domain || account?.domain || null,
      company_logo: account?.company_logo || null,
      company_size: account?.company_size || null,
      location: account?.location || null,
      industry: account?.industry || "",
      similarity_score: score,
      combined_score: score,
      engagement_score: score,
      similarity_reference_name:
        account?.reference_account_name || account?.matched_account || null,
      last_signal_at: account?.last_fetched_at || null,
    };
  });

  const activeCustomQuery = customQueries.find(
    (query) => String(query.id) === String(selectedCustomQueryId),
  ) || customQueries.find((query) => query.is_selected) || customQueries[0] || null;
  const customAccounts = Array.isArray(activeCustomQuery?.recommendations)
    ? activeCustomQuery.recommendations.map((account, index) => ({
      ...account,
      id: account?.recommendation_id || account?.id || `rec-${index}`,
      is_custom_query: true,
      company_name: account?.company_name || account?.company_domain || "Unnamed account",
      company_domain: account?.company_domain || account?.domain || null,
      domain: account?.company_domain || account?.domain || null,
      company_logo: account?.company_logo || null,
      company_size: account?.company_size || account?.headcount || null,
      revenue: account?.revenue || account?.annual_revenue || account?.estimated_revenue || null,
      location: account?.location || null,
      similarity_score: Number(account?.similarity_score) || 0,
      combined_score: Number(account?.similarity_score) || 0,
      engagement_score: Number(account?.similarity_score) || 0,
      last_signal_at: account?.last_fetched_at || null,
      recommendation_status: account?.recommendation_status || account?.status || "available",
    }))
    : [];
  const isCustomQueryRunning = Boolean(
    isRunningCustomQuery || ["queued", "running"].includes(activeCustomQuery?.last_run_status),
  );

  const engagedAccounts = useMemo(
    () => {
      const accounts = Array.isArray(dashboardData?.top_engaged_accounts)
        ? dashboardData.top_engaged_accounts
        : [];
      const references = Array.isArray(dashboardData?.recommendation_seed_accounts)
        ? dashboardData.recommendation_seed_accounts
        : [];
      const referencesByKey = new Map();

      references.forEach((reference) => {
        getAccountKeys(reference).forEach((key) => {
          if (!referencesByKey.has(key)) referencesByKey.set(key, reference);
        });
      });

      return accounts
        .map((account) => {
          const reference = getAccountKeys(account)
            .map((key) => referencesByKey.get(key))
            .find(Boolean);
          const engagementScore = Math.min(
            100,
            Number(account?.engagement_score ?? account?.signal_count) || 0,
          );
          const referenceCommercialScore = reference
            ? Math.min(100, Number(reference.reference_score) || 0)
            : 0;
          const commercialScore = Math.max(
            referenceCommercialScore,
            Math.min(100, Number(account?.commercial_score) || 0),
          );
          const relationshipScore = Math.min(
            100,
            Number(account?.relationship_score) || 0,
          );
          const stakeholderCoverageScore = Math.min(
            100,
            Number(account?.stakeholder_coverage_score) || 0,
          );
          const relationshipCompositeScore = Math.round(
            relationshipScore * 0.6 + stakeholderCoverageScore * 0.4,
          );
          const intentScore = Math.min(100, Number(account?.intent_score) || 0);
          const scoreParts = [
            { score: engagementScore, weight: COMBINED_SCORE_WEIGHTS.engagement },
            { score: commercialScore, weight: commercialScore > 0 ? COMBINED_SCORE_WEIGHTS.commercial : 0 },
            { score: relationshipCompositeScore, weight: relationshipScore > 0 || stakeholderCoverageScore > 0 ? COMBINED_SCORE_WEIGHTS.relationship : 0 },
            { score: intentScore, weight: intentScore > 0 ? COMBINED_SCORE_WEIGHTS.intent : 0 },
          ];
          const availableWeight = scoreParts.reduce((sum, item) => sum + item.weight, 0);
          const combinedScore = Math.round(
            scoreParts.reduce((sum, item) => sum + item.score * item.weight, 0) /
            Math.max(availableWeight, COMBINED_SCORE_WEIGHTS.engagement),
          );

          return {
            ...account,
            engagement_score: combinedScore,
            combined_score: combinedScore,
            engagement_score_raw: engagementScore,
            commercial_score: commercialScore,
            commercial_evidence: getCommercialEvidence(account, reference),
            win_score_breakdown: {
              ...(reference?.score_breakdown || {}),
              won_deals: Number(account?.won_deal_count) > 0 ? 100 : 0,
              open_deals: Math.min(100, (Number(account?.open_deal_count) || 0) * 25),
            },
            is_won: Boolean(account?.account_is_won || reference?.is_won),
            deal_count: Number(account?.deal_count || reference?.deal_count) || 0,
            latest_meeting_at: account?.latest_meeting_at || reference?.latest_meeting_at || null,
          };
        })
        .sort(
          (a, b) =>
            Number(b.combined_score || 0) - Number(a.combined_score || 0) ||
            new Date(b.last_signal_at || 0).getTime() -
            new Date(a.last_signal_at || 0).getTime(),
        );
    },
    [dashboardData],
  );
  const isLookalikeTab = activeTab === "find_look_alikes" || activeTab === "lookalike";
  const isCustomTab = activeTab === "custom_search" || activeTab === "custom";
  const filteredAccounts = engagedAccounts;
  const displayedAccounts =
    isLookalikeTab
      ? lookalikeAccounts
      : isCustomTab
        ? customAccounts
        : filteredAccounts;
  const activeAccount = selectedAccount || displayedAccounts[0] || null;
  const drivers = getDrivers(activeAccount);
  const evidence = getEvidence(activeAccount);
  const activeTabConfig =
    AccountDiscoveryTabs.find((t) => t.key === activeTab) ||
    TABS.find((t) => t.id === activeTab);
  const hasSelectedContact = (account) =>
    account?.contact_ids?.some((id) => createdAccountIds.has(id));

  const openAccount = (account) => {
    if (account?.is_lookalike || account?.is_custom_query) {
      setSelectedAccount(account);
      return;
    }
    const path = getAccountProfilePath(account);
    if (path) navigate(path);
    else setSelectedAccount(account);
  };

  const saveCustomQuery = (event) => {
    event.preventDefault();
    const query = customQueryText.trim();
    if (!query) return;
    createCustomQuery({
      name: customQueryName.trim() || query.slice(0, 80),
      query,
    });
  };

  const createFromContacts = (event, account) => {
    event.stopPropagation();
    if (!user?.id || !account?.contact_ids?.length || hasSelectedContact(account)) {
      return;
    }
    createAccount(
      { userId: user.id, contactIds: account.contact_ids },
      {
        onSuccess: () => {
          setCreatedAccountIds((current) => {
            const next = new Set(current);
            account.contact_ids.forEach((id) => next.add(id));
            return next;
          });
        },
      },
    );
  };

  // Look-alike and engaged rows in the shape the shared results table + drawer read:
  // a stable id, the "why it surfaced" sentence and the evidence as titled sources.
  const toResultRow = (account) => {
    // Sources the research cited come first; then the activity / reference facts we hold.
    const researched = Array.isArray(account?.evidence)
      ? account.evidence.filter((item) => item?.summary || item?.title)
      : [];
    const known = getEvidence(account)
      .filter((item) => item?.value && item.value !== account?.reason)
      .map((item) => ({ title: item.label, summary: item.value, source_url: item.href || null }));
    return {
      ...account,
      id: account?.id || account?.account_id || account?.domain || account?.company_name,
      revenue: account?.revenue || formatUsdMillions(account?.revenue_usd_millions),
      reason: getWhySurfaced(account),
      evidence: [...researched, ...known],
    };
  };
  const lookalikeRows = lookalikeAccounts.map(toResultRow);
  const engagedRows = engagedAccounts.map(toResultRow);

  // Engaged accounts already exist (or are contact clusters): open them, or create the account.
  const renderEngagedAction = (account) => {
    const path = getAccountProfilePath(account);
    if (path) {
      return <TextButton onClick={() => navigate(path)}>Open account</TextButton>;
    }
    if (account?.can_create_account && account?.contact_ids?.length) {
      const started = hasSelectedContact(account);
      return (
        <TextButton
          disabled={started}
          isLoading={isCreatingAccount}
          loadingText="Creating..."
          onClick={(event) => createFromContacts(event, account)}
        >
          {started ? "Research started" : "Create account"}
        </TextButton>
      );
    }
    return null;
  };

  const handleTabClick = (newTab) => {
    navigate(`${basePath}/${newTab}`);
    setActiveTab(newTab);
  };


  return (
    // <div className="flex h-full min-h-0 flex-col overflow-hidden text-slate-800 dark:bg-gray-950 dark:text-gray-100 xl:h-[calc(100dvh-2.5rem)]">
    //   <div className="mx-auto flex min-h-0 w-full max-w-[1800px] flex-1 flex-col gap-3 overflow-hidden">
    //     <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
    //       <div className="flex items-center gap-3">
    //         <button
    //           type="button"
    //           onClick={() => navigate("/accounts")}
    //           className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-white hover:text-gray-800 dark:hover:bg-gray-900 dark:hover:text-gray-100"
    //           aria-label="Back to accounts"
    //         >
    //           <ArrowLeft size={18} />
    //         </button>
    //         <div>
    //           <div className="flex flex-wrap items-baseline gap-3">
    //             <h1 className="text-2xl font-bold tracking-tight">Find new accounts</h1>
    //             <span className="text-sm text-gray-500 dark:text-gray-400">
    //               {formatNumber(Array.isArray(accountsData) ? accountsData.length : 0)} total · {formatNumber(displayedAccounts.length)} in current scope
    //             </span>
    //           </div>
    //           <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
    //             Surface accounts worth acting on from the activity already captured in SalesWorx.
    //           </p>
    //         </div>
    //       </div>
    //       <div className="flex items-center gap-2">
    //         <div className="flex rounded-xl bg-gray-200 p-1 dark:bg-gray-800" role="tablist" aria-label="Discovery mode">
    //           <button
    //             type="button"
    //             role="tab"
    //             aria-selected={mode === "screen"}
    //             onClick={() => setMode("screen")}
    //             className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${mode === "screen" ? "bg-white text-gray-800 shadow-sm dark:bg-gray-700 dark:text-white" : "text-gray-500 dark:text-gray-400"}`}
    //           >
    //             Screen
    //           </button>
    //           <button
    //             type="button"
    //             role="tab"
    //             aria-selected={mode === "logic"}
    //             onClick={() => setMode("logic")}
    //             className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${mode === "logic" ? "bg-white text-gray-800 shadow-sm dark:bg-gray-700 dark:text-white" : "text-gray-500 dark:text-gray-400"}`}
    //           >
    //             Search logic
    //           </button>
    //         </div>
    //         <SecondaryButton
    //           title="Import"
    //           icon={<SlidersHorizontal size={15} />}
    //           disabled
    //           className="hidden sm:flex"
    //         />
    //         <PrimaryButton onClick={() => setShowAddAccountModal(true)}>
    //           Add account
    //         </PrimaryButton>
    //       </div>
    //     </div>

    //     <div className="flex shrink-0 gap-1 overflow-x-auto border-b border-gray-200 dark:border-gray-800">
    //       {TABS.map((tab) => {
    //         const count =
    //           tab.id === "engaged"
    //             ? filteredAccounts.length
    //             : tab.id === "lookalike"
    //               ? similarAccounts.length
    //               : tab.id === "custom"
    //                 ? customAccounts.length
    //                 : null;
    //         const isActive = activeTab === tab.id;
    //         return (
    //           <button
    //             key={tab.id}
    //             type="button"
    //             disabled={!tab.available}
    //             onClick={() => {
    //               if (!tab.available) return;
    //               setActiveTab(tab.id);
    //               setSelectedAccount(null);
    //             }}
    //             className={`flex shrink-0 items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${isActive ? "border-secondary text-gray-900 dark:text-white" : "border-transparent text-gray-500 dark:text-gray-400"} ${!tab.available ? "cursor-not-allowed opacity-60" : "hover:text-gray-800 dark:hover:text-gray-200"}`}
    //             title={!tab.available ? "This discovery mode is planned for a follow-up phase" : undefined}
    //           >
    //             {tab.label}
    //             <span className={`rounded-full px-2 py-0.5 text-xs ${isActive ? "bg-secondary/10 text-secondary" : "bg-gray-100 text-gray-500 dark:bg-gray-800"}`}>
    //               {count == null ? "—" : count}
    //             </span>
    //           </button>
    //         );
    //       })}
    //     </div>

    //     <div className="grid min-h-0 min-w-0 flex-1 gap-4 overflow-hidden xl:grid-cols-[minmax(0,1fr)_360px]">
    //       <section className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
    //         <div className="shrink-0 border-b border-gray-200 px-5 py-4 dark:border-gray-800">
    //           <div className="flex flex-wrap items-center justify-between gap-3">
    //             <div>
    //               <h2 className="font-semibold">
    //                 {activeTab === "lookalike"
    //                   ? "Look-alike accounts"
    //                   : activeTab === "custom"
    //                     ? activeCustomQuery?.name || "Custom query accounts"
    //                     : `${activeTabConfig?.label || "Account discovery"} — last ${windowDays} days`}
    //               </h2>
    //               <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
    //                 {activeTab === "lookalike"
    //                   ? "Net-new companies similar to your strongest engaged and won accounts."
    //                   : activeTab === "custom"
    //                     ? "Net-new companies researched from your saved account criteria."
    //                     : "Engagement and commercial evidence combined into one account-priority score."}
    //               </p>
    //             </div>
    //             <div className="hidden items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 font-mono text-xs text-gray-600 dark:border-gray-700 dark:text-gray-300 md:flex">
    //               <Sparkles size={14} className="text-secondary" /> score 0–100
    //             </div>
    //           </div>
    //         </div>

    //         {mode === "logic" ? (
    //           <div className="border-b border-secondary/20 bg-secondary/5 p-5 dark:bg-secondary/10">
    //             <div className="flex items-start gap-3">
    //               <Info size={19} className="mt-0.5 shrink-0 text-secondary" />
    //               <div>
    //                 <h3 className="font-semibold text-gray-900 dark:text-gray-100">
    //                   {activeTab === "lookalike"
    //                     ? "Current ranking logic: Look-alike accounts"
    //                     : activeTab === "custom"
    //                       ? "Current discovery logic: Saved custom query + public web research"
    //                       : "Current ranking logic: Engagement + likely to win"}
    //                 </h3>
    //                 <p className="mt-1 max-w-4xl text-sm leading-6 text-gray-600 dark:text-gray-300">
    //                   {activeTab === "lookalike"
    //                     ? "Recommendations are generated from the strongest engaged and won accounts, then filtered to net-new companies."
    //                     : activeTab === "custom"
    //                       ? "The saved query is normalized into criteria, researched on the public web, and refreshed daily. Accounts remain recommendations until you add them."
    //                       : "Results combine engagement, commercial evidence, relationship coverage, stakeholder strength, and campaign intent when those signals are available."}
    //                 </p>
    //                 <div className="mt-3 inline-flex rounded-lg border border-secondary/20 bg-white px-3 py-2 font-mono text-xs text-gray-700 dark:bg-gray-900 dark:text-gray-200">
    //                   {activeTab === "lookalike"
    //                     ? "Similarity to strongest accounts · existing accounts excluded"
    //                     : activeTab === "custom"
    //                       ? "AI criteria extraction · public web evidence · daily refresh"
    //                       : "50% engagement + 25% commercial + 15% relationships + 10% intent"}
    //                 </div>
    //               </div>
    //             </div>
    //           </div>
    //         ) : null}

    //         {activeTab === "custom" && mode === "screen" ? (
    //           <div className="shrink-0 border-b border-gray-200 bg-gray-50/50 p-4 dark:border-gray-800 dark:bg-gray-950/40">
    //             <form onSubmit={saveCustomQuery} className="flex flex-col gap-3 lg:flex-row lg:items-end">
    //               <div className="flex min-w-0 flex-1 flex-col gap-1.5">
    //                 <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
    //                   Account research query
    //                 </label>
    //                 <CommonInput
    //                   value={customQueryText}
    //                   onChange={(event) => setCustomQueryText(event.target.value)}
    //                   placeholder="e.g. fintech companies in the UK hiring RevOps"
    //                   wrapperClassName="w-full"
    //                   maxLength={1000}
    //                 />
    //               </div>
    //               <div className="flex w-full flex-col gap-1.5 lg:w-56">
    //                 <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
    //                   Saved query name
    //                 </label>
    //                 <CommonInput
    //                   value={customQueryName}
    //                   onChange={(event) => setCustomQueryName(event.target.value)}
    //                   placeholder="UK RevOps fintech"
    //                   wrapperClassName="w-full"
    //                   maxLength={120}
    //                 />
    //               </div>
    //               <div className="shrink-0">
    //                 <PrimaryButton
    //                   type="submit"
    //                   disabled={!customQueryText.trim() || isCreatingCustomQuery}
    //                   isLoading={isCreatingCustomQuery}
    //                 >
    //                   {isCreatingCustomQuery ? "Researching..." : "Save & research"}
    //                 </PrimaryButton>
    //               </div>
    //             </form>
    //             {customQueries.length > 0 ? (
    //               <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-gray-200/70 pt-3 dark:border-gray-800">
    //                 <div className="flex items-center gap-2">
    //                   <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
    //                     Saved queries:
    //                   </span>
    //                   <CommonSelect
    //                     value={activeCustomQuery?.id || ""}
    //                     onChange={(queryId) => {
    //                       if (!queryId) return;
    //                       setSelectedCustomQueryId(queryId);
    //                       setSelectedAccount(null);
    //                       selectCustomQuery({ queryId });
    //                     }}
    //                     disabled={isSelectingCustomQuery}
    //                     width="w-56"
    //                     options={customQueries.map((query) => ({
    //                       label: query.name,
    //                       value: query.id,
    //                     }))}
    //                     classNames={{
    //                       trigger: "h-9 min-h-9 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1 text-xs text-gray-700 dark:text-gray-200",
    //                       value: "text-xs text-gray-700 dark:text-gray-200",
    //                     }}
    //                   />
    //                 </div>
    //                 {activeCustomQuery ? (
    //                   <button
    //                     type="button"
    //                     onClick={() => runCustomQuery({ queryId: activeCustomQuery.id })}
    //                     disabled={isCustomQueryRunning}
    //                     className="text-xs font-semibold text-secondary hover:underline disabled:opacity-50"
    //                   >
    //                     {isCustomQueryRunning ? "Researching..." : "Run now"}
    //                   </button>
    //                 ) : null}
    //                 {activeCustomQuery?.criteria?.normalized_query ? (
    //                   <span className="truncate text-xs text-gray-500 dark:text-gray-400">
    //                     Criteria: {activeCustomQuery.criteria.normalized_query}
    //                   </span>
    //                 ) : null}
    //               </div>
    //             ) : null}
    //           </div>
    //         ) : null}

    //         {(activeTab === "lookalike"
    //           ? isLoadingSimilarAccounts
    //           : activeTab === "custom"
    //             ? isLoadingCustomQueries || isCustomQueryRunning
    //             : isLoadingDashboard) ? (
    //           <div className="flex min-h-[360px] items-center justify-center">
    //             <Loader
    //               text={
    //                 activeTab === "lookalike"
    //                   ? "Finding look-alike accounts..."
    //                   : activeTab === "custom"
    //                     ? "Researching accounts from the public web..."
    //                     : "Finding engaged and likely-to-win accounts..."
    //               }
    //             />
    //           </div>
    //         ) : (activeTab === "lookalike"
    //           ? isSimilarAccountsError
    //           : activeTab === "custom"
    //             ? isCustomQueriesError
    //             : isDashboardError) ? (
    //           <div className="flex min-h-[360px] items-center justify-center px-5 text-center text-sm text-gray-500 dark:text-gray-400">
    //             We could not load account recommendations right now. Please try again in a moment.
    //           </div>
    //         ) : displayedAccounts.length === 0 ? (
    //           <div className="flex min-h-[360px] flex-col items-center justify-center gap-2 px-5 text-center">
    //             <Search size={24} className="text-gray-400" />
    //             <p className="font-medium">
    //               {activeTab === "lookalike"
    //                 ? "No look-alike accounts found yet"
    //                 : activeTab === "custom"
    //                   ? activeCustomQuery
    //                     ? "No accounts matched this query yet"
    //                     : "Save a custom query to find new accounts"
    //                   : "No accounts match this scope"}
    //             </p>
    //             <p className="text-sm text-gray-500 dark:text-gray-400">
    //               {activeTab === "lookalike"
    //                 ? "Recommendations will appear after a qualifying account is available."
    //                 : activeTab === "custom"
    //                   ? "AI will research public company information and keep the results here until you add an account."
    //                   : "There are no engaged accounts in the current activity window."}
    //             </p>
    //           </div>
    //         ) : (
    //           <div className="min-h-0 flex-1 overflow-auto">
    //             <table className="w-full min-w-[820px] border-collapse text-left">
    //               <thead>
    //                 <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500 dark:border-gray-800 dark:text-gray-400">
    //                   <th className="px-5 py-4 font-semibold">Account</th>
    //                   <th className="px-4 py-4 font-semibold">Industry · Geo</th>
    //                   <th className="px-4 py-4 font-semibold">
    //                     {activeTab === "lookalike" || activeTab === "custom" ? "Fit score" : "Priority score"}
    //                   </th>
    //                   <th className="px-4 py-4 font-semibold">Why it surfaced</th>
    //                   <th className="px-4 py-4 font-semibold">
    //                     {activeTab === "lookalike" || activeTab === "custom" ? "Last researched" : "Last touch"}
    //                   </th>
    //                 </tr>
    //               </thead>
    //               <tbody>
    //                 {displayedAccounts.map((account) => {
    //                   const name = getAccountName(account);
    //                   const score = Number(account?.combined_score ?? account?.engagement_score ?? account?.signal_count) || 0;
    //                   const isSelected = activeAccount === account;
    //                   const canCreate = Boolean(
    //                     !account?.account_id &&
    //                     account?.can_create_account &&
    //                     account?.contact_ids?.length &&
    //                     !hasSelectedContact(account),
    //                   );
    //                   return (
    //                     <tr
    //                       key={`${account?.account_id || account?.domain || name}`}
    //                       onClick={() => setSelectedAccount(account)}
    //                       className={`cursor-pointer border-b border-gray-100 transition-colors dark:border-gray-800/80 ${isSelected ? "bg-secondary/10" : "hover:bg-gray-50 dark:hover:bg-gray-800/60"}`}
    //                     >
    //                       <td className="px-5 py-4 align-top">
    //                         <div className="flex min-w-52 items-start gap-3">
    //                           <CommonAvatar
    //                             imageUrl={sanitizeCompanyLogoUrl(account?.company_logo)}
    //                             name={getCleanName(name)}
    //                             size="lg"
    //                             className="rounded-xl"
    //                           />
    //                           <div className="min-w-0">
    //                             <button
    //                               type="button"
    //                               onClick={(event) => {
    //                                 event.stopPropagation();
    //                                 openAccount(account);
    //                               }}
    //                               className="block max-w-[220px] truncate text-sm font-semibold text-gray-800 hover:text-secondary dark:text-gray-100"
    //                             >
    //                               {name}
    //                             </button>
    //                             <p className="mt-1 max-w-[220px] truncate text-xs text-gray-500 dark:text-gray-400">
    //                               {account?.domain || "Domain not identified"}{account?.company_size ? ` · ${account.company_size} emp` : ""}
    //                             </p>
    //                             {account?.is_lookalike || account?.is_custom_query ? (
    //                               <div className="mt-2">
    //                                 <SimilarAccountActionButton account={account} />
    //                               </div>
    //                             ) : canCreate ? (
    //                               <button
    //                                 type="button"
    //                                 onClick={(event) => createFromContacts(event, account)}
    //                                 disabled={isCreatingAccount}
    //                                 className="mt-2 text-xs font-semibold text-secondary hover:underline disabled:opacity-50"
    //                               >
    //                                 {isCreatingAccount ? "Creating..." : "Create account"}
    //                               </button>
    //                             ) : hasSelectedContact(account) ? (
    //                               <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-secondary">
    //                                 <Check size={13} /> Research started
    //                               </span>
    //                             ) : null}
    //                           </div>
    //                         </div>
    //                       </td>
    //                       <td className="px-4 py-4 align-top text-sm">
    //                         <p className="font-medium text-gray-700 dark:text-gray-200">{getIndustry(account)}</p>
    //                         <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{getDisplayLocation(account?.location)}</p>
    //                       </td>
    //                       <td className="w-36 px-4 py-4 align-top">
    //                         <div className="flex items-center justify-between gap-2 text-sm font-semibold">
    //                           <span>{Math.round(score)}</span>
    //                           <span className="text-xs font-normal text-gray-400">/100</span>
    //                         </div>
    //                         <div className="mt-2"><ScoreBar value={score} /></div>
    //                       </td>
    //                       <td className="max-w-[290px] px-4 py-4 align-top text-sm text-gray-600 dark:text-gray-300">
    //                         <p className="line-clamp-2">{getWhySurfaced(account)}</p>
    //                         <div className="mt-2 flex flex-wrap gap-1.5">
    //                           {getDominantTopEngagedChannel(account?.signal_counts) ? (
    //                             <span className="rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">
    //                               {topEngagedChannelTitle(getDominantTopEngagedChannel(account?.signal_counts))}
    //                             </span>
    //                           ) : null}
    //                           {account?.signal_counts?.reply ? (
    //                             <span className="rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">
    //                               {account.signal_counts.reply} replies
    //                             </span>
    //                           ) : null}
    //                           {account?.is_lookalike && account?.similarity_reference_name ? (
    //                             <span className="rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">
    //                               Based on {account.similarity_reference_name}
    //                             </span>
    //                           ) : null}
    //                           {account?.is_custom_query && account?.industry ? (
    //                             <span className="rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">
    //                               {account.industry}
    //                             </span>
    //                           ) : null}
    //                         </div>
    //                       </td>
    //                       <td className="whitespace-nowrap px-4 py-4 align-top text-sm text-gray-500 dark:text-gray-400">
    //                         {account?.last_signal_at
    //                           ? safeFormatTimeAgo(account.last_signal_at)
    //                           : "—"}
    //                       </td>
    //                     </tr>
    //                   );
    //                 })}
    //               </tbody>
    //             </table>
    //           </div>
    //         )}
    //       </section>

    //       <aside className="flex min-h-0 min-w-0 flex-col overflow-y-auto rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
    //         {activeAccount ? (
    //           <>
    //             <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 pb-4 dark:border-gray-800">
    //               <div className="flex min-w-0 items-center gap-3">
    //                 <CommonAvatar
    //                   imageUrl={sanitizeCompanyLogoUrl(activeAccount?.company_logo)}
    //                   name={getCleanName(getAccountName(activeAccount))}
    //                   size="lg"
    //                   className="rounded-xl"
    //                 />
    //                 <div className="min-w-0">
    //                   <p className="text-xs font-bold uppercase tracking-[0.14em] text-secondary">
    //                     {activeAccount?.is_lookalike || activeAccount?.is_custom_query ? "Why this surfaced" : "Why this account"}
    //                   </p>
    //                   <h2 className="mt-1 truncate text-lg font-bold">{getAccountName(activeAccount)}</h2>
    //                   <p className="mt-1 truncate text-sm text-gray-500 dark:text-gray-400">{getIndustry(activeAccount)} · {getDisplayLocation(activeAccount?.location)}</p>
    //                 </div>
    //               </div>
    //               <div className="shrink-0 text-right">
    //                 <p className="text-3xl font-bold text-secondary">{Number(activeAccount?.combined_score ?? activeAccount?.engagement_score) || 0}</p>
    //                 <p className="text-xs text-gray-500 dark:text-gray-400">
    //                   {activeAccount?.is_lookalike || activeAccount?.is_custom_query ? "Fit score" : "Priority score"}
    //                 </p>
    //               </div>
    //             </div>

    //             <div className="shrink-0 border-b border-gray-200 py-4 dark:border-gray-800">
    //               <h3 className="text-sm font-semibold">Score drivers</h3>
    //               <div className="mt-4 space-y-4">
    //                 {drivers.length ? drivers.map((driver) => (
    //                   <div key={driver.key}>
    //                     <div className="mb-1.5 flex items-center justify-between gap-3 text-xs">
    //                       <span className="text-gray-600 dark:text-gray-300">{driver.label}{driver.detail ? ` · ${driver.detail}` : ""}</span>
    //                       <span className="font-semibold text-gray-700 dark:text-gray-200">{driver.displayValue}</span>
    //                     </div>
    //                     <ScoreBar value={Math.min(100, driver.points * 2)} className="bg-slate-700 dark:bg-slate-400" />
    //                   </div>
    //                 )) : (
    //                   <p className="text-sm text-gray-500 dark:text-gray-400">Detailed score inputs are not available for this account.</p>
    //                 )}
    //               </div>
    //             </div>

    //             <div className="py-4">
    //               <h3 className="text-sm font-semibold">Evidence</h3>
    //               <div className="mt-3 space-y-3">
    //                 {evidence.length ? evidence.map((item) => (
    //                   <div key={`${item.label}-${item.value}`} className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/60">
    //                     <p className="text-sm text-gray-700 dark:text-gray-200">{item.value}</p>
    //                     {item.href ? (
    //                       <a
    //                         href={item.href}
    //                         target="_blank"
    //                         rel="noopener noreferrer"
    //                         className="mt-2 inline-block text-xs font-medium text-secondary hover:underline"
    //                       >
    //                         {item.label}
    //                       </a>
    //                     ) : (
    //                       <p className="mt-2 text-xs font-medium text-secondary">{item.label}</p>
    //                     )}
    //                   </div>
    //                 )) : (
    //                   <p className="text-sm text-gray-500 dark:text-gray-400">No supporting evidence is available yet.</p>
    //                 )}
    //               </div>
    //             </div>

    //             {activeAccount?.is_lookalike || activeAccount?.is_custom_query ? (
    //               <div className="mt-auto flex shrink-0 items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800/60">
    //                 <span className="text-sm font-medium">Add to accounts</span>
    //                 <SimilarAccountActionButton account={activeAccount} />
    //               </div>
    //             ) : getAccountProfilePath(activeAccount) ? (
    //               <PrimaryButton
    //                 onClick={() => openAccount(activeAccount)}
    //                 className="mt-auto shrink-0 w-full"
    //                 startContent={false}
    //               >
    //                 Open account
    //               </PrimaryButton>
    //             ) : null}
    //           </>
    //         ) : (
    //           <div className="flex min-h-[300px] flex-1 flex-col items-center justify-center text-center">
    //             <Sparkles size={28} className="text-secondary" />
    //             <h2 className="mt-3 font-semibold">Select an account</h2>
    //             <p className="mt-1 max-w-xs text-sm text-gray-500 dark:text-gray-400">Choose a result to inspect the activity that caused it to surface.</p>
    //           </div>
    //         )}
    //       </aside>
    //     </div>
    //   </div>

    //   <AddAccountModal
    //     isOpen={showAddAccountModal}
    //     onClose={() => setShowAddAccountModal(false)}
    //   />
    // </div>
    <div className={embedded ? "flex flex-col gap-4 h-full" : "flex flex-col gap-4"}>
      {!embedded && (
        <>
          <BreadCrumbs
            items={[
              { label: "Accounts", href: "/accounts" },
              { label: "Find new accounts" },

            ]}
          />

          <div className="pl-6 pr-3 w-full border-t border-gray-200 dark:border-gray-800 flex items-center gap-3">
            <SquircleIcon>
              <Search />
            </SquircleIcon>
            <div className="text-base font-medium font-sans text-black dark:text-white">Find New Accounts</div>
            <p className="text-sm font-normal text-gray-500 dark:text-gray-400">
              Find new companies or surface ones already showing activity
            </p>
          </div>
        </>
      )}

      {/* tabs */}
      <UnderlineTabs
        tab={activeTab}
        selectedKey={activeTab}
        handleTabClick={handleTabClick}
        onSelectionChange={handleTabClick}
        tabs={AccountDiscoveryTabs}
        // Inside the Find page the main tabs already draw the line above.
        className={embedded ? "!border-t-0" : ""}
      />
      <div
        className={` bg-white dark:bg-gray-900 w-full ${embedded ? "flex-1 min-h-0" : "h-[calc(100dvh-186px)]"} overflow-y-auto scrollbar-hide`}
      >
        {
          (activeTab === "custom_search" || activeTab === "custom") && (
            showSearchResults ? (
              <CustomSearchResultsTable
                queryName={activeCustomQuery?.name || (customQueryText ? customQueryText.slice(0, 30) : "Custom Search")}
                queryText={activeCustomQuery?.query || customQueryText}
                onChangeQueryText={setCustomQueryText}
                onSearch={(query) => {
                  const trimmed = (query || customQueryText).trim();
                  if (!trimmed) return;
                  setCustomQueryText(trimmed);
                  createCustomQuery({
                    name: trimmed.slice(0, 80),
                    query: trimmed,
                  });
                }}
                customQueries={customQueries}
                selectedCustomQueryId={selectedCustomQueryId || activeCustomQuery?.id}
                onSelectQuery={(query) => {
                  if (!query) return;
                  setSelectedCustomQueryId(query.id);
                  selectCustomQuery({ queryId: query.id });
                  setCustomQueryText(query.query || "");
                }}
                onRunQuery={(query) => {
                  const targetId = query?.id || activeCustomQuery?.id;
                  if (!targetId) return;
                  runCustomQuery({ queryId: targetId });
                }}
                onNewSearch={() => {
                  setShowSearchResults(false);
                  setCustomQueryText("");
                }}
                isLoading={isCreatingCustomQuery || isCustomQueryRunning}
                // A brand-new search must not show the previous query's rows while it loads.
                accounts={isCreatingCustomQuery ? [] : customAccounts}
              />
            ) : (
              <CustomSearch
                value={customQueryText}
                onChange={setCustomQueryText}
                customQueries={customQueries}
                selectedCustomQueryId={selectedCustomQueryId}
                onSelectQuery={(query) => {
                  if (!query) return;
                  setSelectedCustomQueryId(query.id);
                  selectCustomQuery({ queryId: query.id });
                  setCustomQueryText(query.query || "");
                  setShowSearchResults(true);
                }}
                onRunQuery={(query) => {
                  if (!query?.id) return;
                  runCustomQuery({ queryId: query.id });
                }}
                onSearch={(queryText) => {
                  const query = (queryText || customQueryText).trim();
                  if (!query) return;
                  setCustomQueryText(query);
                  setShowSearchResults(true);
                  createCustomQuery({
                    name: customQueryName.trim() || query.slice(0, 80),
                    query,
                  });
                }}
                isLoading={isCreatingCustomQuery}
              />
            )
          )
        }

        {isLookalikeTab && (
          <DiscoveryResultsTable
            accounts={lookalikeRows}
            isLoading={isLoadingSimilarAccounts}
            isError={isSimilarAccountsError}
            loadingText="Finding look-alike accounts"
            emptyTitle="No look-alike accounts found yet"
            emptyDescription="Recommendations will appear after a qualifying account is available."
          />
        )}

        {activeTab === "most_engaged_plus_likely_to_win" && (
          <DiscoveryResultsTable
            accounts={engagedRows}
            isLoading={isLoadingDashboard}
            isError={isDashboardError}
            loadingText="Finding engaged and likely-to-win accounts"
            emptyTitle="No accounts match this scope"
            emptyDescription={`There are no engaged accounts in the last ${windowDays} days.`}
            renderAction={renderEngagedAction}
            scoreLabel={(score) => `${score} Priority`}
          />
        )}
      </div>
    </div>
  );
}

export { AccountDiscoveryTabs };
